/** The campaign the game ships with, and how far through it a player is.
 *
 * The scenarios are *generated* rather than hand-drawn, and that is a deliberate
 * trade rather than a shortcut. A hand-authored campaign is six maps somebody
 * has to draw, and until this afternoon there was no editor to draw them in; a
 * generated one is a template, a seed and a briefing, and it is the same map
 * every time because the generator is deterministic in its seed. What a player
 * gets is a real campaign - a chain, a carry-over, a bonus choice, a difficulty
 * curve - on boards nobody sat and composed. When somebody does compose one,
 * `parseCampaign` will read it and nothing here has to change.
 *
 * Progress lives in local storage beside the saves, for the same reason the
 * save index does: it is a host fact, and there is nowhere in a `GameState` for
 * "this is the third map of six" to live without inventing one.
 */

import type {
  CampaignFile, CampaignProgress, CampaignScenario, ContentRegistry, GameState,
  NewGameSetup, PlayerId,
} from "@game/core";
import {
  CAMPAIGN_FORMAT_VERSION, CAMPAIGN_KIND, applyBonus, applyCarry, carryFrom,
  newGame, startProgress,
} from "@game/core";
import { generateMap, findTemplate, toObjectSetups } from "@game/content/mapgen";

import { PLAYER_COLOURS, startingHero } from "./newgame.js";
import { startingResourcesFor } from "./controllers.js";

const PROGRESS_KEY = "realm.campaign.v1";

// ------------------------------------------------------------- the campaign

/** One leg: which template, which seed, and what the player is told. */
interface Leg {
  readonly id: string;
  readonly name: string;
  readonly briefing: string;
  readonly template: string;
  readonly seed: number;
  readonly opponents: number;
  readonly faction: string;
  readonly carryHeroes: number;
  readonly bonuses: CampaignScenario["bonuses"];
}

/**
 * Four legs, and the shape of them is the campaign.
 *
 * A duel to learn the map on, a land grab that introduces neutral towns, a
 * three-cornered war where the player is no longer the only aggressor, and a
 * long one on a large board against three. The hero carries forward from the
 * second leg on, which is when he has enough levels for it to be worth
 * anything - carrying a level-three hero into map two is a rounding error and
 * makes the wrenchhand look pointless the first time a player meets it.
 */
const LEGS: readonly Leg[] = Object.freeze([
  {
    id: "landfall",
    name: "Landfall",
    briefing:
      "One rival, one small continent, and nothing between you but the ground. "
      + "Take his capital before he takes yours. Whatever you finish this map with, "
      + "the hero who ends it comes with you.",
    template: "duel", seed: 101, opponents: 1, faction: "sunspire",
    carryHeroes: 0,
    bonuses: [
      { kind: "resources", label: "A full purse: 10,000 gold", resources: { gold: 10000 } },
      { kind: "resources", label: "Timber and ore: 20 of each", resources: { wood: 20, ore: 20 } },
    ],
  },
  {
    id: "the-crossroads",
    name: "The Crossroads",
    briefing:
      "Open country, three unclaimed towns in the middle of it, and a rival who "
      + "wants them as much as you do. There is a cavern under all of this; whoever "
      + "holds the gates holds the shortest road.",
    template: "crossroads", seed: 202, opponents: 1, faction: "sunspire",
    carryHeroes: 1,
    bonuses: [
      { kind: "resources", label: "15,000 gold", resources: { gold: 15000 } },
      { kind: "creatures", label: "A company of 40 spearwardens", creature: "sunspire.spearwarden", count: 40 },
    ],
  },
  {
    id: "three-crowns",
    name: "Three Crowns",
    briefing:
      "Two rivals now, and neither of them is waiting for you. The board is the "
      + "same size and there is a third more war on it. Your hero and everything he "
      + "is carrying come with you.",
    template: "crossroads", seed: 303, opponents: 2, faction: "sunspire",
    carryHeroes: 1,
    bonuses: [
      { kind: "resources", label: "20,000 gold", resources: { gold: 20000 } },
      { kind: "resources", label: "A full stock of rare ore", resources: { mercury: 10, sulfur: 10, crystal: 10, gems: 10 } },
    ],
  },
  {
    id: "the-long-war",
    name: "The Long War",
    briefing:
      "A large board, three rivals, and everything on it fighting twice as hard as "
      + "it did on the first map. This is the one the last three were for.",
    template: "hard-country", seed: 404, opponents: 3, faction: "sunspire",
    carryHeroes: 2,
    bonuses: [
      { kind: "resources", label: "30,000 gold", resources: { gold: 30000 } },
      { kind: "creatures", label: "A wing of 12 skyclaws", creature: "sunspire.skyclaw", count: 12 },
    ],
  },
]);

export const SHIPPED_CAMPAIGN_ID = "the-road-east";

/**
 * Build the campaign file.
 *
 * Every map is generated here rather than at play time so that the scenario
 * list, the player counts and the board sizes are all knowable from the
 * campaign screen before a single map is started - and so that the file this
 * produces is a real `CampaignFile` that `serializeCampaign` could write to
 * disk and `parseCampaign` read back.
 */
export function shippedCampaign(content: ContentRegistry): CampaignFile {
  return {
    kind: CAMPAIGN_KIND,
    formatVersion: CAMPAIGN_FORMAT_VERSION,
    contentVersion: content.contentVersion,
    meta: {
      name: "The Road East",
      description:
        "Four maps, each harder than the last, and the hero who wins one begins the "
        + "next. Generated boards - the same four every time.",
      author: "",
    },
    scenarios: LEGS.map((leg) => ({
      id: leg.id,
      name: leg.name,
      briefing: leg.briefing,
      setup: legSetup(content, leg),
      bonuses: leg.bonuses,
      carry: {
        heroes: leg.carryHeroes,
        experience: true, skills: true, artifacts: true, spells: true,
      },
    })),
  };
}

function legSetup(content: ContentRegistry, leg: Leg): NewGameSetup {
  const template = findTemplate(leg.template);
  const players = leg.opponents + 1;
  const size = template?.size ?? 72;
  const gen = generateMap(leg.seed, {
    ...(template?.options ?? {}),
    content, width: size, height: size, players,
    // The player's own faction is pinned so the carried hero stays plausible;
    // the rest are the generator's roll.
    factions: [leg.faction as never],
  });

  const takenHeroes = new Set<string>();
  return {
    rootSeed: leg.seed,
    map: gen.map,
    players: gen.startingPositions.map((s, i) => {
      const town = gen.towns[s.town];
      const hero = startingHero(content, s.faction as string, leg.seed + i * 31, takenHeroes);
      takenHeroes.add(hero as string);
      return {
        id: `p${String(i + 1)}` as PlayerId,
        kind: i === 0 ? "human" as const : "ai" as const,
        colour: PLAYER_COLOURS[i] ?? "red",
        faction: s.faction,
        towns: town === undefined
          ? []
          : [{ faction: s.faction, x: town.x, y: town.y, z: town.z, garrison: town.garrison }],
        heroes: [{
          def: hero,
          x: s.heroX, y: s.heroY, z: s.heroZ,
        }],
      };
    }),
    neutralTowns: gen.towns.filter((t) => t.player < 0).map((t) => ({
      faction: t.faction, x: t.x, y: t.y, z: t.z, built: t.built, garrison: t.garrison,
    })),
    objects: toObjectSetups(gen),
    victory: "defeat_all",
    contentVersion: content.contentVersion,
  };
}

// -------------------------------------------------------------- one leg

export const HUMAN: PlayerId = "p1" as PlayerId;

/**
 * Build the game for one leg: the map, then the carry-over, then the bonus.
 *
 * That order matters. The carry-over writes onto heroes `newGame` has already
 * minted, so it has to come after; the bonus goes to the first hero by id and
 * would otherwise land on a hero the carry-over is about to overwrite.
 */
export function startLeg(
  scenario: CampaignScenario, progress: CampaignProgress,
  content: ContentRegistry, bonusIndex: number, difficulty: number,
): GameState {
  const setup: NewGameSetup = {
    ...scenario.setup,
    players: scenario.setup.players.map((p, i) => (
      i === 0 ? { ...p, resources: startingResourcesFor(difficulty) } : p
    )),
  };
  let state = newGame(setup, content);
  state = applyCarry(state, HUMAN, progress.carried, content);
  state = applyBonus(state, HUMAN, scenario.bonuses[bonusIndex] ?? null, content);
  return state;
}

/** Advance the progress past a leg the player has won. */
export function advanceProgress(
  progress: CampaignProgress, file: CampaignFile, won: GameState, bonusLabel: string,
): CampaignProgress {
  const next = progress.at + 1;
  const following = file.scenarios[next];
  return {
    campaign: progress.campaign,
    at: next,
    // What travels is decided by the scenario being entered, not the one being
    // left: "what does this map let you bring" is the map's own rule.
    carried: following === undefined ? [] : carryFrom(won, HUMAN, following.carry),
    bonuses: [...progress.bonuses, bonusLabel],
  };
}

// ------------------------------------------------------------------ storage

function storage(): Storage | null {
  try {
    const s = globalThis.localStorage;
    s.getItem(PROGRESS_KEY);
    return s;
  } catch {
    return null;
  }
}

export function readProgress(campaign = SHIPPED_CAMPAIGN_ID): CampaignProgress {
  const s = storage();
  if (s === null) return startProgress(campaign);
  try {
    const raw = s.getItem(PROGRESS_KEY);
    if (raw === null) return startProgress(campaign);
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") return startProgress(campaign);
    const p = parsed as Partial<CampaignProgress>;
    if (p.campaign !== campaign || typeof p.at !== "number") return startProgress(campaign);
    return {
      campaign,
      at: Math.max(0, Math.trunc(p.at)),
      carried: Array.isArray(p.carried) ? p.carried : [],
      bonuses: Array.isArray(p.bonuses) ? p.bonuses : [],
    };
  } catch {
    return startProgress(campaign);
  }
}

export function writeProgress(progress: CampaignProgress): void {
  const s = storage();
  if (s === null) return;
  try {
    s.setItem(PROGRESS_KEY, JSON.stringify(progress));
  } catch { /* progress that will not persist is not worth failing a game over */ }
}

export function clearProgress(): void {
  const s = storage();
  if (s === null) return;
  try {
    s.removeItem(PROGRESS_KEY);
  } catch { /* nothing to clear */ }
}
